/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prettier/prettier */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useMemo } from 'react';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import '@glidejs/glide/src/assets/sass/glide.core.scss';
import '@glidejs/glide/src/assets/sass/glide.theme.scss';
import 'react-loading-skeleton/dist/skeleton.css';

import '../styles/cobrand.scss';
import rtlPlugin from 'stylis-plugin-rtl';
import { StyleSheetManager } from 'styled-components';
import NextApp from 'next/app';
import { sessionTimeoutManager } from '@marriott/shared/mi-helper-utils';
import { FEATURE_FLAGS, fetchFlag, TARGETS } from '@marriott/mi-feature-flag-utils';
import { ApolloProvider } from '@apollo/client';
import { getDeployedEnvType, useNextMiApolloClient } from '@marriott/mi-apollo-client-utils';

import { defineComponentMapping, getAllowedComponentsForNonAuthorMode } from '../import-components';
import path from 'path';
import {
  canUseDOM,
  CustomModelClient,
  getEnvProp,
  populatePIdataLayer,
  respGridUtil,
  UserSessionProvider,
  RENDERER,
  DynamicDCAPageProvider,
} from '@marriott/mi-headless-utils';
import {
  useDCAPageModelStore,
  useClientEnvVarsStore,
  PageModelStoreType,
  PageModelState,
  createPageModelStore,
  PageModelContext,
} from '@marriott/mi-store-utils';
path.resolve('./next.config.js');
import { envVarKeys } from '../_constants/envVarKeys';
import { RESOLVED_URL } from '../_constants/constants';
import { Types } from '@marriott/mi-ui-library';
import { version as globalStylesVersion } from '@marriott/global-styles/package.json';

declare global {
  interface Window {
    jQuery: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $: any;
    deployedEnvType: string;
    dataLayer?: Record<string, unknown>;
    targetGlobalSettings: Record<string, unknown>;
  }
}

const modelClient = new CustomModelClient(process.env.NEXT_PUBLIC_AEM_HOST);

if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync({
    modelClient,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const App = (props: any) => {
  const { Component, pageProps } = props;
  const {
    model,
    isAuthorMode,
    resourceUrl,
    query,
    requestId,
    NEXT_PUBLIC_FETCH_OFFERS_API_URL,
    showError,
    apolloEnvVars,
    resolvedUrl,
    currentLocale,
    TARGET_PROPERTY_TOKEN,
    NEXT_GLOBAL_ASSET_PREFIX,
    featureFlagSessionTimeout,
    sessionTimeoutMs,
    operationSignatures,
    featureFlagLogo,
    featureFlagLegacyNav,
  } = pageProps;
  const modelAllowedComponents = model?.cqItems?.root?.[':items']?.responsivegrid?.allowedComponents?.components;
  // Wrap with useMemo to avoid recomputing on every render
  const allowedComponents = useMemo(() => {
    if (!model) return undefined;
    if ((modelAllowedComponents?.length ?? 0) > 0 || isAuthorMode) return modelAllowedComponents;
    return getAllowedComponentsForNonAuthorMode(model);
  }, [model, modelAllowedComponents, isAuthorMode]);
  const isStickyCardEnabled = model?.enableStickyHeader && model?.enableStickyHeader === 'true' ? true : false;
  const isGlobalNavSticky = model?.enableStickyGlobalNav === true || model?.enableStickyGlobalNav === 'true';
  const isCardComparisonFeatureEnabled =
    model?.enableCardComparisonFeature && model?.enableCardComparisonFeature === 'true';
  const dataLayer = pageProps?.dataLayer;
  const respGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid, { normalizeTracking: true });
  // Deep clone is expensive for large AEM models (credit-cards page model is ~8k lines).
  // Memoize so the JSON.parse(JSON.stringify(...)) only runs when the model actually changes,
  // instead of on every render pass.
  const respGridModelClone = useMemo(() => JSON.parse(JSON.stringify(respGridModel)), [respGridModel]); //To deep clone the model
  const store = useRef<PageModelStoreType>();
  // Track whether the AEM component mapping has already been registered.
  // MapTo(...) writes to a global registry, so the ~70 registrations only need to
  // run once per session in published mode. Guarding this avoids re-running the full
  // mapping on every re-render (a main-thread cost on the hydration path).
  const componentMappingInitialized = useRef(false);
  const initialPageModel: PageModelState = {
    pageModel: respGridModel,
  };
  const isChasePage = resolvedUrl?.includes(RESOLVED_URL?.CHASE_CREDIT_CARD_URL);
  const isAmexPage = resolvedUrl?.includes(RESOLVED_URL?.AMEX_CREDIT_CARD_URL);
  const isRtl = currentLocale === 'ar-AE';
  const cobrandCards = model?.cobrandCards;
  const ecmCardPriority = model?.ECM_CARD_PRIORITY;
  const ecmUserCards = model?.ECM_USER_CARDS;
  const ecmCardMapping = model?.ECM_CARD_MAPPING;
  const ecmSelectedCardParam = model?.ECM_SELECTED_CARD_PARAM;

  if (model?.seo && model?.seo.uri) {
    const allUri = model?.seo.uri;
    const langList = Object.keys(allUri);
    model['hreflangList'] = langList?.map(key => ({
      href: allUri[key],
      hreflang: key,
    }));
  }

  if (!store.current) {
    store.current = createPageModelStore(initialPageModel);
  }

  // Get sessionData from global store
  const isClient = typeof window !== 'undefined';

  // Check if user is logged in - only on client side
  const isLoggedIn = React.useMemo(() => {
    if (!isClient) return false;

    const sessionData = (window as any).sessionData;
    return (
      sessionData?.data?.AriesCommon?.memState === 'authenticated' ||
      sessionData?.cacheData?.data?.AriesCommon?.memState === 'authenticated'
    );
  }, [isClient]);

  useEffect(() => {
    if (
      !isAuthorMode &&
      typeof window !== 'undefined' &&
      featureFlagSessionTimeout['experience_mcom_session_timeout_migration'] &&
      sessionTimeoutMs > 0
    ) {
      // console.log('Session Timeout - initializing with timeout:', sessionTimeoutMs);
      sessionTimeoutManager.initialize({
        timeoutMs: sessionTimeoutMs,
        isLoggedIn,
        onTimeout() {
          if (typeof window === 'undefined') return;

          // console.log('Session related App specific functionality can be added here');
        },
        onUserActivity() {
          //  console.log('User activity detected - session timer reset');
        },
      });
    }
  }, [sessionTimeoutMs, isLoggedIn, isAuthorMode, featureFlagSessionTimeout]);

  const setPageModel = useDCAPageModelStore(state => state.setPageModel);
  if (setPageModel) {
    setPageModel(respGridModelClone);
  }
  const rtlPluginTs = (...args: unknown[]) => {
    return rtlPlugin(args[0] as number, args[1] as string);
  };

  const envVars = apolloEnvVars ? apolloEnvVars : {};
  envVars['NEXT_PUBLIC_FETCH_OFFERS_API_URL'] = NEXT_PUBLIC_FETCH_OFFERS_API_URL;
  envVars['NEXT_PUBLIC_ENABLE_WEBSDK'] =
    getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_WEBSDK) === 'true' && model?.enableWebSDK === 'true';
  envVars['ENABLE_ACDL'] = model?.['ENABLE_ACDL'];
  envVars['ENABLE_ECM'] = model?.['ENABLE_ECM'];
  envVars['REQUEST_ID'] = requestId;
  envVars['ACCEPT_LANGUAGE'] = currentLocale;
  const setEnvVars = useClientEnvVarsStore(state => state.setEnvVars);
  envVars['NEXT_PUBLIC_ENABLE_DCA_V2'] = getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_DCA_V2);
  envVars['TARGET_PROPERTY_TOKEN'] = TARGET_PROPERTY_TOKEN;
  const MiApolloClient = useNextMiApolloClient(
    operationSignatures || [],
    envVars?.DEPLOYED_ENV_TYPE || getDeployedEnvType(),
    {},
    {},
    envVars
  );
  useEffect(() => {
    // The global stylesheet is already server-rendered in _document.tsx <Head> for all modes,
    // so we only need to inject it client-side in AEM author mode (where _document head styles
    // are not applied to the authored iframe). Injecting it unconditionally previously caused a
    // duplicate stylesheet request and a late style recalculation/FOUC after hydration.
    if (!isAuthorMode) {
      return;
    }
    const assetUrl = `https://www.marriott.com/mi-assets/mi-global/`;
    const globalStyle = document.createElement('link');

    globalStyle.rel = 'stylesheet';
    globalStyle.href =
      assetUrl +
      (model?.rtlEnable == 'true'
        ? `global-styles/${globalStylesVersion}/marriot.global.rtl.min.css`
        : `global-styles/${globalStylesVersion}/marriot.global.min.css`);
    document.head.appendChild(globalStyle);
  }, [isAuthorMode, model?.rtlEnable]);
  useEffect(() => {
    if (model?.templateName === 'headless-next-js-cdp-template') {
      window.targetGlobalSettings = {
        aepSandboxId: '4b4e08f2-8e19-4baf-8e08-f28e19fbafe2',
        aepSandboxName: 'aepdevelopment',
      };
    }
  }, [model]);

  if (setEnvVars) {
    setEnvVars(envVars);
  }

  if (canUseDOM) {
    populatePIdataLayer();
  }

  // Keep component mapping synchronous (NOT deferred to an effect) so AEM mapped
  // components render on the first pass with no FOUC. In author mode we re-register
  // eagerly on every render because the authored model changes during editing. In
  // published mode the registry is stable, so we register once per session via the ref.
  const registerComponentMapping = () =>
    defineComponentMapping(
      allowedComponents,
      resourceUrl,
      isAuthorMode,
      dataLayer,
      {
        isStickyCardEnabled,
        allQueryParams: query,
        isCardComparisonFeatureEnabled,
        isGlobalNavSticky,
      },
      showError,
      resolvedUrl,
      {
        isTextCentered: isChasePage,
        featureTitleFontSize: isChasePage ? Types.size.large : Types.size.extraLarge,
        isTickIconFilled: isChasePage,
      },
      {
        isChaseButton: isChasePage,
        isAmexButton: isAmexPage,
        isTertiaryLinksVisible: isAmexPage,
        isImagehidden: isAmexPage,
        isPointDescriptionVisible: isAmexPage,
        isBonusPointsLabelVisible: isAmexPage,
        isAnnualFeeVisible: isAmexPage,
      },
      isRtl,
      { featureFlagLogo, featureFlagLegacyNav },
      { cobrandCards, ecmCardPriority, ecmUserCards, ecmCardMapping, ecmSelectedCardParam }
    );

  if (isAuthorMode) {
    registerComponentMapping();
  } else if (!componentMappingInitialized.current) {
    registerComponentMapping();
    componentMappingInitialized.current = true;
  }
  Object.defineProperty(rtlPluginTs, 'name', { value: 'rtlPluginMonorepo' });
  return (
    // For ACDL setup - @see https://marriottcloud.atlassian.net/browse/DXP-16772
    <UserSessionProvider pageProps={pageProps} includePiData={true} renderer={RENDERER.COBRAND}>
      <PageModelContext.Provider value={store.current}>
        <StyleSheetManager stylisPlugins={pageProps?.model?.rtlEnable == 'true' ? [rtlPluginTs] : []}>
          <ApolloProvider client={MiApolloClient}>
            <DynamicDCAPageProvider Component={Component} pageProps={pageProps} />
          </ApolloProvider>
        </StyleSheetManager>
      </PageModelContext.Provider>
    </UserSessionProvider>
  );
};

// Fetch feature flag at the app level so it applies to all routes
App.getInitialProps = async (appContext: any) => {
  // Call page's getInitialProps if it exists
  const appProps = await NextApp.getInitialProps(appContext);

  // Fetch session timeout feature flag server-side
  const featureFlagSessionTimeout = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_SESSION_TIMEOUT_MIGRATION, {
    target: TARGETS.APP.COBRAND_RENDERER,
    enableStream: true,
  });

  // Get session timeout from runtime env var (only available server-side)
  const sessionTimeoutMs = process.env.NEXT_PUBLIC_SESSION_TIMEOUT_MS
    ? parseInt(process.env.NEXT_PUBLIC_SESSION_TIMEOUT_MS, 10) * 1000
    : 0;

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      featureFlagSessionTimeout,
      sessionTimeoutMs,
    },
  };
};

export default App;
